<template>
  <div class="signup">
    <h1>Create an Account</h1>
    <p class="subtitle">By creating an account on Curatoreden you'll be able to buy, sell, comment, and more.</p>
    
    <div v-if="error" class="error">
      {{ error }}
    </div>
    
    <div v-if="success" class="success">
      {{ success }}
    </div>

    <div class="social-buttons">
      <button class="google-btn" @click="continueWithGoogle" :disabled="loading">
        <img src="https://www.google.com/favicon.ico" alt="Google" class="google-icon" />
        {{ loading ? 'Connecting...' : 'Continue with Google' }}
      </button>
    </div>

    <div class="divider">
      <span>OR</span>
    </div>
    
    <form @submit.prevent="signup">
      <div class="form-group">
        <input 
          type="email" 
          id="email"
          v-model="email"
          placeholder="Email"
          required
        />
      </div>
      
      <div class="form-group">
        <input 
          type="password" 
          id="password"
          v-model="password"
          placeholder="Password"
          required
        />
      </div>
      
      <button type="submit" :disabled="loading" class="signup-btn">
        {{ loading ? 'Creating Account...' : 'CREATE ACCOUNT WITH EMAIL' }}
      </button>
    </form>

    <!--
    <div class="terms">
      By creating an account, I accept Curatoreden's <a href="#" @click.prevent>Terms of Service</a>
    </div>
    -->

    <div class="login-link">
      Already have an account? <a href="#" @click.prevent="openLoginModal">Log In</a>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';

export default {
  name: 'Signup',
  data() {
    return {
      email: '',
      password: '',
      loading: false,
      error: null,
      success: null,
      googleAuthUrl: `${config.apiBaseUrl}/api/auth/google` // Google OAuth URL
    }
  },
  methods: {
    async signup() {
      this.loading = true;
      this.error = null;
      this.success = null;
      
      try {
        const response = await axios.post(`${config.apiBaseUrl}/api/auth/signup`, {
          email: this.email,
          password: this.password
        });

        this.success = 'Account created successfully!';
        
        // Session-based auth - no localStorage needed
        
        this.$root.$emit('auth-state-changed', {
          isLoggedIn: true,
          user: response.data.user
        });

        this.$emit('signup-success');
        
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to create account. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    openLoginModal() {
      this.$emit('close');
      this.$root.$emit('open-login-modal');
    },
    async continueWithGoogle() {
      try {
        // Open Google OAuth in a popup window
        const width = 500;
        const height = 600;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        
        const popup = window.open(
          this.googleAuthUrl,
          'Google Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Listen for messages from the popup
        window.addEventListener('message', async (event) => {
          if (event.origin === config.apiBaseUrl && event.data.type === 'google-auth') {
            // Close the popup
            popup.close();

            if (event.data.error) {
              this.error = event.data.error;
              return;
            }

            // Handle successful Google login
            const { token, user } = event.data;
            
            // Session-based auth - no localStorage needed
            
            // Update auth state
            this.$root.$emit('auth-state-changed', {
              isLoggedIn: true,
              user: user
            });

            this.success = 'Successfully signed in with Google!';
            
            // Emit signup success
            this.$emit('signup-success');
          }
        });
      } catch (error) {
        console.error('Google auth error:', error);
        this.error = 'Failed to authenticate with Google. Please try again.';
      }
    }
  }
}
</script>

<style scoped>
.signup {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 20px;
}

h1 {
  text-align: center;
  margin-bottom: 15px;
  color: #000;
  font-size: 24px;
  font-weight: 500;
}

.subtitle {
  text-align: center;
  color: #666;
  margin-bottom: 25px;
  font-size: 14px;
  line-height: 1.4;
}

.social-buttons {
  margin-bottom: 20px;
}

.google-btn {
  width: 100%;
  padding: 12px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  background: white;
  color: #3c4043;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background-color 0.2s, box-shadow 0.2s;
}

.google-btn:hover {
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  background: #f8f9fa;
}

.google-icon {
  width: 18px;
  height: 18px;
}

.divider {
  text-align: center;
  margin: 20px 0;
  position: relative;
}

.divider::before,
.divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 45%;
  height: 1px;
  background: #ddd;
}

.divider::before {
  left: 0;
}

.divider::after {
  right: 0;
}

.divider span {
  background: white;
  padding: 0 10px;
  color: #666;
  font-size: 14px;
}

.form-group {
  margin-bottom: 15px;
}

input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

input:focus {
  outline: none;
  border-color: #000;
}

input::placeholder {
  color: #999;
}

.signup-btn {
  width: 100%;
  padding: 12px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-top: 10px;
}

.signup-btn:hover:not(:disabled) {
  background: #333;
}

.signup-btn:disabled {
  background: #666;
  cursor: not-allowed;
}

.error {
  color: #721c24;
  background: #f8d7da;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
  font-size: 14px;
}

.success {
  color: #155724;
  background: #d4edda;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
  font-size: 14px;
}

.terms {
  text-align: center;
  margin-top: 20px;
  font-size: 12px;
  color: #666;
}

.terms a {
  color: #000;
  text-decoration: none;
  font-weight: 500;
}

.terms a:hover {
  text-decoration: underline;
}

.login-link {
  text-align: center;
  margin-top: 15px;
  font-size: 14px;
  color: #666;
}

.login-link a {
  color: #000;
  text-decoration: none;
  font-weight: 500;
}

.login-link a:hover {
  text-decoration: underline;
}
</style> 